<template>
    <div>
        <b-form-row>
            <!-- The maxlength values mirror the PatientPlans column widths. Without them the save
                 truncates an over-long identifier to fit and still reports success, which on a
                 billing identifier means every later claim on the plan bills the wrong value. -->
            <b-col>
                <b-form-group label="Cardholder Id"
                              invalid-feedback="Sent on every claim billed against this plan.">
                    <b-form-input v-model="patientPlan.cardholderId"
                                  maxlength="20"
                                  required
                                  :state="cardholderIdState"></b-form-input>
                </b-form-group>
            </b-col>
            <b-col>
                <b-form-group label="Group Id">
                    <b-form-input v-model="patientPlan.groupId" maxlength="15"></b-form-input>
                </b-form-group>
            </b-col>
            <b-col>
                <b-form-group label="Person Code">
                    <b-form-input v-model="patientPlan.personCode" maxlength="3"></b-form-input>
                </b-form-group>
            </b-col>
            <b-col>
                <EnumSelector label="Relationship Code"
                              label-class="col-form-label pt-0"
                              v-model="patientPlan.relationshipCode"
                              :enumCodes="relationshipCodes"></EnumSelector>
            </b-col>
        </b-form-row>
        <b-form-row>
            <b-col cols="6" md="3">
                <b-form-group>
                    <template #label>
                        DOB Override
                        <HelpPopup>
                            <p>When set, this date is sent to insurance in place of the patient's date of birth.</p>
                        </HelpPopup>
                    </template>
                    <!-- The reset button always emits "", never null: b-form-datepicker coerces any
                         reset-value through `formatYMD(...) || ''`. Consumers normalize before saving. -->
                    <b-form-datepicker v-model="patientPlan.dateOfBirthOverride"
                                       reset-button
                                       placeholder="No override"></b-form-datepicker>
                </b-form-group>
            </b-col>
            <b-col cols="6" md="5">
                <EnumSelector label="Eligibility Clarification Code"
                              label-class="col-form-label pt-0"
                              v-model="patientPlan.eligibilityClarificationCode"
                              :enumCodes="eligibilityClarificationCodes"
                              :elementsToDiscard="discardedEligibilityClarificationCodes"
                              help-html="<p>When set, this eligibility override is sent to insurance on every claim billed against this plan.</p>"
                              clearable></EnumSelector>
            </b-col>
        </b-form-row>
        <b-form-row>
            <b-col cols="6" md="3">
                <b-form-group>
                    <template #label>
                        Date of Injury
                        <HelpPopup>
                            <p>When set, this date is sent to insurance on every claim billed against this plan (Workers Comp).</p>
                        </HelpPopup>
                    </template>
                    <b-form-datepicker v-model="patientPlan.dateOfInjury"
                                       reset-button
                                       :max="maxDateOfInjury"
                                       placeholder="Not set"></b-form-datepicker>
                </b-form-group>
            </b-col>
        </b-form-row>
    </div>
</template>

<script lang="ts">
    import Component from "vue-class-component";
    import {Vue, VModel} from "vue-property-decorator";
    import {EligibilityClarificationCode, PatientPlan, RelationshipCode} from "@/models/PatientPlan";
    import EnumSelector from "@/components/EnumSelector.vue";
    import HelpPopup from "@/components/HelpPopup.vue";

    @Component({
        components: {
            EnumSelector,
            HelpPopup,
        }
    })
    export default class PatientPlanEditComponent extends Vue {
        @VModel() private patientPlan!: PatientPlan;
        readonly relationshipCodes = RelationshipCode;
        readonly eligibilityClarificationCodes = EligibilityClarificationCode;
        // Clearing the selector is how "no override" is expressed; an explicit 0 would be transmitted.
        readonly discardedEligibilityClarificationCodes = [EligibilityClarificationCode.NotSpecified];

        // A Date of Injury can't logically be in the future, and this value auto-submits on every
        // claim for the plan, so cap the picker at today to make a mistyped future year unrepresentable.
        get maxDateOfInjury(): Date {
            return new Date();
        }

        // Flagged rather than blocked: this component is also hosted on the Rx billing screen, where
        // a save gate would be a behaviour change beyond the plan list. An emptied cardholder id
        // transmits an empty NCPDP 302-C2 and every later claim on the plan rejects, so the field is
        // marked at the point of edit. A valid value yields null, not true, so the input is left
        // unstyled instead of turning green.
        get cardholderIdState(): boolean | null {
            return this.patientPlan?.cardholderId ? null : false;
        }
    }
</script>

<style scoped>
</style>
